/**
 * Vector PDF via backend Playwright → real app route /planning-info/print/:sessionId.
 * Avoids html2canvas (screenshots) and HTML snapshots (broken RTL).
 */
import type { AppLanguage } from '@/i18n/translations';
import { fetchPlanningReportPdf } from '@/lib/api/assessment';

export const DOCH33_REPORT_SELECTOR = '#doch33-report';

export type PlanningReportPdfOptions = {
  filename: string;
  sessionId: string;
  lang?: AppLanguage;
};

function downloadBlob(blob: Blob, filename: string): boolean {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  return true;
}

/** Opens the print-only route so the user can Save as PDF (vector) from the browser. */
export function openPlanningReportPrintPreview(
  sessionId: string,
  lang?: AppLanguage,
  options?: { autoprint?: boolean },
): void {
  const params = new URLSearchParams();
  if (lang) params.set('lang', lang);
  if (options?.autoprint) params.set('autoprint', '1');
  const query = params.toString();
  const url = `${window.location.origin}/planning-info/print/${sessionId}${query ? `?${query}` : ''}`;
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  if (!win) {
    throw new Error('Popup blocked — allow popups to open the print preview.');
  }
}

/**
 * Downloads a vector PDF rendered from the live React report page.
 * Returns the blob so callers can offer a manual download link if auto-download is blocked.
 */
export async function exportPlanningReportPdf(
  options: PlanningReportPdfOptions,
): Promise<Blob> {
  const blob = await fetchPlanningReportPdf(options.sessionId, {
    baseUrl: window.location.origin,
    lang: options.lang,
  });
  downloadBlob(blob, options.filename);
  return blob;
}

/** @deprecated Use exportPlanningReportPdf — kept for call-site compatibility */
export async function exportPlanningReportPdfFromHtml(
  _source: HTMLElement,
  options: PlanningReportPdfOptions,
): Promise<void> {
  await exportPlanningReportPdf(options);
}

export function getDoch33ReportElement(): HTMLElement | null {
  return document.querySelector<HTMLElement>(DOCH33_REPORT_SELECTOR);
}
